import { useState } from "react";
import { putExclusiveBenefitsForTenant } from "../../../../api/exclusiveBenefits";
import { reportError } from "../../../../api/errors";
import { useAdminData } from "../../AdminDataContext";
import { benefitsStore, CATEGORY_LABELS, type ExclusiveBenefit, type BenefitCategory } from "../../utils/benefitsStore";

const CATEGORY_OPTIONS: { value: BenefitCategory; label: string; color: string }[] = [
  { value: 'rights', label: '权利类', color: 'bg-red-50 text-red-600' },
  { value: 'activity', label: '活动类', color: 'bg-amber-50 text-amber-600' },
  { value: 'discount', label: '优惠类', color: 'bg-green-50 text-green-600' },
  { value: 'service', label: '服务类', color: 'bg-teal-50 text-teal-600' },
];

const CATEGORY_COLORS: Record<BenefitCategory, string> = {
  rights: 'bg-red-50 text-red-600',
  activity: 'bg-amber-50 text-amber-600',
  discount: 'bg-green-50 text-green-600',
  service: 'bg-teal-50 text-teal-600',
};

const ICON_OPTIONS = [
  'ri-government-line', 'ri-vip-crown-line', 'ri-user-star-line', 'ri-coin-line',
  'ri-medal-line', 'ri-award-line', 'ri-ticket-2-line', 'ri-ticket-line',
  'ri-calendar-check-line', 'ri-calendar-line', 'ri-percent-line', 'ri-price-tag-3-line',
  'ri-lock-unlock-line', 'ri-file-text-line', 'ri-check-double-line', 'ri-file-2-line',
  'ri-coupon-line', 'ri-gift-2-line', 'ri-map-pin-line', 'ri-quill-pen-line',
  'ri-shield-star-line', 'ri-star-line', 'ri-heart-line', 'ri-group-line',
];

interface FormState {
  name: string;
  icon: string;
  category: BenefitCategory;
  description: string;
}

const emptyForm: FormState = { name: '', icon: 'ri-star-line', category: 'rights', description: '' };

export default function BenefitsConfig() {
  const { tenantLegacyId, reload } = useAdminData();
  const [benefits, setBenefits] = useState<ExclusiveBenefit[]>(() => benefitsStore.getAll());
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [filterCat, setFilterCat] = useState<BenefitCategory | 'all'>('all');

  const refresh = () => setBenefits(benefitsStore.getAll());

  const persistBenefits = async (): Promise<boolean> => {
    try {
      const next = await putExclusiveBenefitsForTenant(tenantLegacyId, benefitsStore.getAll());
      benefitsStore.replaceFromApi(next);
      refresh();
      void reload({ silent: true });
      return true;
    } catch (e) {
      reportError(e);
      void reload({ silent: true });
      return false;
    }
  };

  const openAdd = () => { setForm(emptyForm); setEditingId('new'); };
  const openEdit = (b: ExclusiveBenefit) => {
    setForm({ name: b.name, icon: b.icon, category: b.category, description: b.description?.trim() ?? '' });
    setEditingId(b.id);
  };
  const closeForm = () => { setEditingId(null); setForm(emptyForm); };

  const handleSave = () => {
    void (async () => {
      if (!form.name.trim()) return;
      if (editingId === 'new') {
        benefitsStore.add(form);
      } else if (editingId) {
        benefitsStore.update(editingId, form);
      }
      refresh();
      if (await persistBenefits()) closeForm();
    })();
  };

  const handleDelete = (id: string) => {
    void (async () => {
      benefitsStore.remove(id);
      refresh();
      setDeleteId(null);
      await persistBenefits();
    })();
  };

  const filtered = filterCat === 'all' ? benefits : benefits.filter((b) => b.category === filterCat);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-bold text-gray-900">专属权益字典</h3>
          <p className="text-xs text-gray-400 mt-0.5">定义协会会员专属权益条目，在等级字典中可为每个等级勾选配置</p>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm rounded-xl hover:bg-red-700 cursor-pointer transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line"></i> 新增权益
        </button>
      </div>

      <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-6 flex gap-3">
        <i className="ri-information-line text-amber-500 text-base flex-shrink-0 mt-0.5"></i>
        <div className="text-xs text-amber-700 space-y-1">
          <p className="font-medium">使用说明</p>
          <p>• 此字典中的权益条目可在「会员等级」配置页中为每个等级自由勾选</p>
          <p>• 权益分为权利类、活动类、优惠类、服务类四大分类，便于管理</p>
          <p>• 修改权益名称后，已配置该权益的等级中也会同步更新</p>
        </div>
      </div>

      {/* Category filter */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {[{ value: 'all' as const, label: '全部', color: 'bg-gray-600' }, ...CATEGORY_OPTIONS.map((c) => ({ value: c.value, label: c.label, color: 'bg-red-600' }))].map((cat) => (
          <button
            key={cat.value}
            onClick={() => setFilterCat(cat.value)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${
              filterCat === cat.value
                ? 'bg-red-600 text-white'
                : 'bg-white text-gray-600 border border-gray-100 hover:bg-gray-50'
            }`}
          >
            {cat.label}
            <span className="ml-1 opacity-70">
              {cat.value === 'all' ? benefits.length : benefits.filter((b) => b.category === cat.value).length}
            </span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {filtered.map((b) => (
          <div key={b.id} className={`bg-white rounded-2xl border p-4 flex items-center gap-3 group transition-all ${b.enabled === false ? "opacity-60 border-gray-100" : "border-gray-100"}`}>
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${b.enabled === false ? "bg-gray-100" : "bg-gray-50"}`}>
              <i className={`${b.icon} text-lg ${b.enabled === false ? "text-gray-300" : "text-gray-500"}`}></i>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="text-sm font-semibold text-gray-800 leading-tight truncate">{b.name}</p>
                {b.enabled === false && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-400 rounded-full flex-shrink-0">已关闭</span>
                )}
              </div>
              {b.description?.trim() && (
                <p className="text-[11px] text-gray-500 line-clamp-2 mt-0.5 leading-snug">{b.description.trim()}</p>
              )}
              <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded-full mt-1 font-medium ${CATEGORY_COLORS[b.category]}`}>
                {CATEGORY_LABELS[b.category]}
              </span>
            </div>
            <div className="flex flex-col items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
              {/* Enable/disable toggle */}
              <button
                onClick={() => {
                  benefitsStore.toggleEnabled(b.id);
                  refresh();
                  void persistBenefits();
                }}
                title={b.enabled === false ? "启用" : "禁用"}
                className={`relative rounded-full transition-colors cursor-pointer ${b.enabled !== false ? "bg-red-500" : "bg-gray-300"}`}
                style={{ width: 32, height: 18 }}
              >
                <span
                  className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full shadow transition-all ${b.enabled !== false ? "left-3.5" : "left-0.5"}`}
                  style={{ width: 14, height: 14 }}
                />
              </button>
              <div className="flex gap-1">
                <button
                  onClick={() => openEdit(b)}
                  className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-amber-600 cursor-pointer rounded-lg hover:bg-amber-50 transition-colors"
                >
                  <i className="ri-edit-line text-xs"></i>
                </button>
                <button
                  onClick={() => setDeleteId(b.id)}
                  className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-red-600 cursor-pointer rounded-lg hover:bg-red-50 transition-colors"
                >
                  <i className="ri-delete-bin-line text-xs"></i>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <i className="ri-gift-2-line text-3xl mb-2 block text-gray-200"></i>
          <p className="text-sm">暂无权益条目</p>
          <button onClick={openAdd} className="mt-3 text-xs text-red-600 cursor-pointer hover:underline">添加第一条权益</button>
        </div>
      )}

      {/* Add/Edit Modal */}
      {editingId !== null && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-[480px] max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-gray-800">{editingId === 'new' ? '新增专属权益' : '编辑专属权益'}</h3>
              <button onClick={closeForm} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">权益名称 *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="如：VIP专属看台区域"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">权益描述</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  placeholder="补充说明权益内容、使用条件或注意事项（选填）"
                  rows={3}
                  maxLength={2000}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 resize-none leading-relaxed"
                />
                <p className="text-[10px] text-gray-400 mt-1">最多 2000 字；会员端可在权益列表中展示</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">权益分类</label>
                <div className="flex gap-2 flex-wrap">
                  {CATEGORY_OPTIONS.map((c) => (
                    <button
                      key={c.value}
                      onClick={() => setForm((p) => ({ ...p, category: c.value }))}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap border-2 ${
                        form.category === c.value ? 'border-gray-800' : 'border-transparent'
                      } ${c.color}`}
                    >
                      {form.category === c.value && <i className="ri-check-line mr-1 text-xs"></i>}
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">权益图标</label>
                <div className="grid grid-cols-8 gap-1.5">
                  {ICON_OPTIONS.map((ic) => (
                    <button
                      key={ic}
                      onClick={() => setForm((p) => ({ ...p, icon: ic }))}
                      className={`w-9 h-9 flex items-center justify-center rounded-xl text-base cursor-pointer transition-all ${
                        form.icon === ic ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      <i className={ic}></i>
                    </button>
                  ))}
                </div>
              </div>

              {/* Preview */}
              <div className="bg-gray-50 rounded-xl p-3 flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${form.icon} text-base text-gray-500`}></i>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-800">{form.name || '权益名称预览'}</p>
                  {form.description.trim() ? (
                    <p className="text-[11px] text-gray-500 mt-1 leading-snug line-clamp-3">{form.description.trim()}</p>
                  ) : null}
                  <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded-full font-medium mt-1 ${CATEGORY_COLORS[form.category]}`}>
                    {CATEGORY_LABELS[form.category]}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6 pt-5 border-t border-gray-100">
              <button onClick={closeForm} className="flex-1 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">
                取消
              </button>
              <button
                onClick={handleSave}
                disabled={!form.name.trim()}
                className="flex-1 py-2.5 text-sm text-white bg-red-600 rounded-xl hover:bg-red-700 cursor-pointer disabled:opacity-50 whitespace-nowrap"
              >
                {editingId === 'new' ? '创建权益' : '保存修改'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-72">
            <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center mb-4 mx-auto">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="font-bold text-gray-800 text-center mb-2">删除权益条目</h3>
            <p className="text-sm text-gray-500 text-center mb-5 leading-relaxed">
              删除后，已配置此权益的等级将不再显示该条目。
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">取消</button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 py-2.5 text-sm text-white bg-red-600 rounded-xl hover:bg-red-700 cursor-pointer whitespace-nowrap">确认删除</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
